import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import { TrackingEventData } from '../../../hooks/useTracking';
import { EventLabels } from '../../../utils/trackingEvents';
import Modal from '../../Modal';
import Button, { ButtonVariant } from '../Button';
import Text, { TextColor, TextKind } from '../Text';

const ConfirmationContainer = styled.div`
  width: 500px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  padding: 16px;
`;

const ConfirmationButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  titleIcon?: React.ReactElement<typeof FontAwesomeIcon>;
  description?: string | React.ReactElement;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmButtonVariant?: ButtonVariant;
  confirmButtonEventLabelOverride?: EventLabels;
  cancelButtonEventDataLabelOverride?: EventLabels;
  confirmationDisabled?: boolean;
  confirmButtonLoading?: boolean;
  testId?: string;
  trackingData?: TrackingEventData;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure you want to continue?',
  titleIcon,
  description,
  cancelLabel = 'Cancel',
  confirmLabel = 'Continue',
  confirmButtonVariant = ButtonVariant.Dark,
  confirmButtonEventLabelOverride,
  cancelButtonEventDataLabelOverride,
  confirmationDisabled,
  confirmButtonLoading,
  testId = 'confirmation-modal',
  trackingData,
}) => (
  <Modal
    testId={testId}
    isOpen={isOpen}
    onClose={onClose}
    withPadding={false}
    width="auto"
    trackingData={trackingData}
  >
    <ConfirmationContainer>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {titleIcon && titleIcon}
        <Text kind={TextKind.TitleS}>{title}</Text>
      </div>
      {description && typeof description === 'string' && (
        <Text
          kind={TextKind.BodyL}
          color={TextColor.Secondary}
          testId="confirmation-modal-description"
        >
          {description}
        </Text>
      )}
      {description && typeof description !== 'string' && (
        <DescriptionContainer data-testid="confirmation-modal-description">
          {description}
        </DescriptionContainer>
      )}
      <ConfirmationButtonContainer>
        <Button
          testId="confirmation-modal-cancel-button"
          variant={ButtonVariant.Outline}
          onClick={onClose}
          trackingData={{
            event_label:
              cancelButtonEventDataLabelOverride ?? EventLabels.Nevermind,
            ...trackingData,
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          testId="confirmation-modal-confirm-button"
          variant={confirmButtonVariant}
          onClick={onConfirm}
          disabled={confirmationDisabled}
          loading={confirmButtonLoading}
          trackingData={{
            event_label:
              confirmButtonEventLabelOverride ?? EventLabels.YesCancel,
            ...trackingData,
          }}
        >
          {confirmLabel}
        </Button>
      </ConfirmationButtonContainer>
    </ConfirmationContainer>
  </Modal>
);
